import React, { useState } from "react";
import {
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TextField,
  Select,
  MenuItem,
  Chip,
  Typography,
  Button,
  TablePagination,
  TableSortLabel,
  Divider,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import ReplayIcon from "@mui/icons-material/Replay";
import { visuallyHidden } from "@mui/utils";
import ModalComponent from "./Modal/Modal";

const getAssignStatusChip = (status, label) => {
  switch (status) {
    case "pending":
      return <Chip size="small" label={label || "Pendiente"} color="default" />;
    case "incomplete":
      return <Chip size="small" label={label || "Incompleto"} color="warning" />;
    case "complete":
      return <Chip size="small" label={label || "Completado"} color="success" />;
    case "time_expired":
      return <Chip size="small" label={label || "Tiempo expirado"} color="error" />;
    default:
      return <Chip size="small" label={label || status} variant="outlined" />;
  }
};

const getQuestionnaireStatusChip = (status, label) => {
  switch (status) {
    case "pending":
      return (
        <Chip
          size="small"
          label={label || "Pendiente"}
          sx={{
            bgcolor: "#787878",
            color: "#fff",
            fontWeight: 600,
          }}
        />
      );
    case "started":
      return (
        <Chip
          size="small"
          label={label || "Iniciado"}
          sx={{
            bgcolor: "#27A6E6",
            color: "#fff",
            fontWeight: 600,
          }}
        />
      );
    case "finished":
      return (
        <Chip
          size="small"
          label={label || "Terminado"}
          sx={{
            bgcolor: "#539160",
            color: "#fff",
            fontWeight: 600,
          }}
        />
      );
    case "not_evaluable":
      return (
        <Chip
          size="small"
          label={label || "No evaluable"}
          sx={{
            bgcolor: "#9E9673",
            color: "#fff",
            fontWeight: 600,
          }}
        />
      );
    case "withdrawn":
      return (
        <Chip
          size="small"
          label={label || "Retirado"}
          sx={{
            bgcolor: "#E58740",
            color: "#fff",
            fontWeight: 600,
          }}
        />
      );
    default:
      return <Chip size="small" label={label || status} variant="outlined" />;
  }
};

const formatDateTime = (isoString) => {
  if (!isoString) return "-";
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString();
};

const assignStatusOptions = [
  { value: "pending", label: "Pendiente" },
  { value: "incomplete", label: "Incompleto" },
  { value: "complete", label: "Completado" },
  { value: "time_expired", label: "Tiempo expirado" },
];

const questionnaireStatusOptions = [
  { value: "pending", label: "Pendiente" },
  { value: "started", label: "Iniciado" },
  { value: "finished", label: "Terminado" },
  { value: "not_evaluable", label: "No evaluable" },
  { value: "withdrawn", label: "Retirado" },
];

const questionnaireTypeOptions = [
  { value: "general", label: "General" },
  { value: "clinical", label: "Clínica" },
  { value: "project", label: "Proyecto de grado" },
  { value: "usability", label: "Usabilidad" },
  { value: "withdrawal", label: "Retiro" },
  { value: "psychology", label: "Psicología" },
  { value: "rotation", label: "Rotación" },
  { value: "tutoring", label: "Tutoría" },
  { value: "not_evaluable", label: "No evaluable" },
];

const renderContextContent = (context) => {
  if (context == null || (typeof context === "object" && Object.keys(context).length === 0)) {
    return (
      <Typography variant="body2" color="text.secondary" fontStyle="italic">
        Sin contexto
      </Typography>
    );
  }
  if (typeof context === "string") {
    return <Typography variant="body2">{context.trim() || "Sin contexto"}</Typography>;
  }
  if (typeof context === "object") {
    return (
      <Box sx={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "6px 16px" }}>
        {Object.entries(context).map(([key, value]) => (
          <React.Fragment key={key}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, whiteSpace: "nowrap" }}>
              {key}:
            </Typography>
            <Typography variant="body2" sx={{ wordBreak: "break-word", whiteSpace: "pre-wrap" }}>
              {typeof value === "object" ? JSON.stringify(value, null, 2) : String(value)}
            </Typography>
          </React.Fragment>
        ))}
      </Box>
    );
  }
  return <Typography variant="body2">{String(context)}</Typography>;
};

const AssignmentsTable = ({
  rows,
  totalCount,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  filters,
  onFilterChange,
  onApplyFilters,
  onClearFilters,
  sortBy,
  sortDir,
  onSortChange,
  onReactivate,
}) => {
  const [contextModal, setContextModal] = useState({ open: false, row: null });

  const handleSort = (field) => {
    const isSameField = sortBy === field;
    const nextDir = isSameField && sortDir === "asc" ? "desc" : "asc";
    onSortChange(field, nextDir);
  };

  const SortHeader = ({ field, label }) => (
    <TableSortLabel active={sortBy === field} direction={sortBy === field ? sortDir : "asc"} onClick={() => handleSort(field)}>
      {label}
      {sortBy === field ? (
        <Box component="span" sx={visuallyHidden}>
          {sortDir === "desc" ? "ordenado descendente" : "ordenado ascendente"}
        </Box>
      ) : null}
    </TableSortLabel>
  );

  const canReactivate = (status) => status === "finished" || status === "withdrawn";

  return (
    <Box>
      <Box sx={{ mb: 1, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
        <Typography variant="body2" color="text.secondary">
          Mostrando {rows.length} de {totalCount} registros
        </Typography>

        <Box sx={{ display: "flex", gap: 1 }}>
          <Button size="small" variant="contained" onClick={onApplyFilters} sx={{ textTransform: "none" }}>
            Aplicar filtros
          </Button>

          <Button
            size="small"
            variant="text"
            color="secondary"
            startIcon={<ClearAllIcon fontSize="small" />}
            onClick={onClearFilters}
            sx={{ textTransform: "none" }}
          >
            Limpiar filtros
          </Button>
        </Box>
      </Box>

      <TableContainer sx={{ maxHeight: 600 }}>
        <Table stickyHeader size="small" aria-label="tabla de asignaciones">
          <TableHead
            sx={{
              "& .MuiTableCell-root": {
                fontWeight: "bold",
                bgcolor: "background.paper",
              },

              "& tr:first-of-type .MuiTableCell-root": {
                position: "static",
                zIndex: 1,
              },

              "& tr:nth-of-type(2) .MuiTableCell-root": {
                position: "sticky",
                top: 0,
                zIndex: 3,
                bgcolor: "background.paper",
              },
            }}
          >
            {/* Fila de filtros */}
            <TableRow>
              <TableCell>
                <TextField
                  variant="standard"
                  placeholder="ID externo..."
                  fullWidth
                  value={filters.aqExternalId}
                  onChange={(e) => onFilterChange("aqExternalId", e.target.value)}
                  InputProps={{ style: { fontSize: 12 } }}
                />
              </TableCell>

              <TableCell>
                <TextField
                  variant="standard"
                  placeholder="Nombre o correo..."
                  fullWidth
                  value={filters.user}
                  onChange={(e) => onFilterChange("user", e.target.value)}
                  InputProps={{ style: { fontSize: 12 } }}
                />
              </TableCell>

              <TableCell>
                <TextField
                  variant="standard"
                  placeholder="Título o ID externo..."
                  fullWidth
                  value={filters.questionnaireSearch}
                  onChange={(e) => onFilterChange("questionnaireSearch", e.target.value)}
                  InputProps={{ style: { fontSize: 12 } }}
                />
              </TableCell>

              <TableCell>
                <Select
                  variant="standard"
                  displayEmpty
                  fullWidth
                  value={filters.questionnaireType}
                  onChange={(e) => onFilterChange("questionnaireType", e.target.value)}
                  sx={{ fontSize: 12 }}
                >
                  <MenuItem value="" sx={{ fontSize: 12 }}>
                    Todos
                  </MenuItem>
                  {questionnaireTypeOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: 12 }}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </TableCell>

              <TableCell>
                <Select
                  variant="standard"
                  displayEmpty
                  fullWidth
                  value={filters.questionnaireStatus}
                  onChange={(e) => onFilterChange("questionnaireStatus", e.target.value)}
                  sx={{ fontSize: 12 }}
                >
                  <MenuItem value="" sx={{ fontSize: 12 }}>
                    Todos
                  </MenuItem>
                  {questionnaireStatusOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: 12 }}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </TableCell>

              <TableCell>
                <TextField
                  variant="standard"
                  placeholder="Filtrar contexto..."
                  fullWidth
                  value={filters.context}
                  onChange={(e) => onFilterChange("context", e.target.value)}
                  InputProps={{ style: { fontSize: 12 } }}
                />
              </TableCell>

              <TableCell>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                  <TextField
                    variant="standard"
                    type="date"
                    label="Desde"
                    fullWidth
                    value={filters.windowFrom}
                    onChange={(e) => onFilterChange("windowFrom", e.target.value)}
                    InputProps={{ style: { fontSize: 11 } }}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    variant="standard"
                    type="date"
                    label="Hasta"
                    fullWidth
                    value={filters.windowTo}
                    onChange={(e) => onFilterChange("windowTo", e.target.value)}
                    InputProps={{ style: { fontSize: 11 } }}
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>
              </TableCell>

              <TableCell>
                <Select
                  variant="standard"
                  displayEmpty
                  fullWidth
                  value={filters.assignStatus}
                  onChange={(e) => onFilterChange("assignStatus", e.target.value)}
                  sx={{ fontSize: 12 }}
                >
                  <MenuItem value="" sx={{ fontSize: 12 }}>
                    Todos
                  </MenuItem>
                  {assignStatusOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: 12 }}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </TableCell>

              <TableCell />
            </TableRow>

            {/* Fila de encabezados*/}
            <TableRow>
              <TableCell>
                <SortHeader field="aq_external_id" label="ID externo asignación cuestionario" />
              </TableCell>

              <TableCell>
                <SortHeader field="user_name" label="Usuario" />
              </TableCell>

              <TableCell>
                <SortHeader field="questionnaire_title" label="Cuestionario (título / ID externo)" />
              </TableCell>

              <TableCell>
                <SortHeader field="questionnaire_type" label="Tipo cuestionario" />
              </TableCell>

              <TableCell>
                <SortHeader field="questionnaire_status" label="Estado cuestionario" />
              </TableCell>

              <TableCell>
                <SortHeader field="context" label="Contexto" />
              </TableCell>

              <TableCell>
                <SortHeader field="assign_window_start" label="Ventana" />
              </TableCell>

              <TableCell>
                <SortHeader field="assign_status" label="Estado aplicación" />
              </TableCell>

              <TableCell>Acciones</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>
                  <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}>
                    {row.aqExternalId || "-"}
                  </Typography>
                </TableCell>

                <TableCell>
                  <Typography variant="body2">{row.userName}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {row.userEmail}
                  </Typography>
                </TableCell>

                <TableCell>
                  <Typography variant="body2">{row.questionnaireTitle}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    ID externo: {row.questionnaireExternalId || "-"}
                  </Typography>
                </TableCell>

                <TableCell>
                  <Chip size="small" variant="outlined" label={row.questionnaireTypeLabel || row.questionnaireType} />
                </TableCell>

                <TableCell>{getQuestionnaireStatusChip(row.questionnaireStatus, row.questionnaireStatusLabel)}</TableCell>

                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setContextModal({ open: true, row })}
                    disabled={!row.questionnaireContext}
                    startIcon={<InfoOutlinedIcon fontSize="small" />}
                    sx={{ textTransform: "none", fontSize: "0.75rem" }}
                  >
                    Ver contexto
                  </Button>
                </TableCell>

                <TableCell>
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography variant="caption" color="text.secondary">
                      Inicio: {formatDateTime(row.assignActivationWindowStart)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Fin: {formatDateTime(row.assignActivationWindowEnd)}
                    </Typography>
                  </Box>
                </TableCell>

                <TableCell>{getAssignStatusChip(row.assignStatus, row.assignStatusLabel)}</TableCell>

                <TableCell>
                  {canReactivate(row.questionnaireStatus) && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="warning"
                      onClick={() => onReactivate(row)}
                      startIcon={<ReplayIcon fontSize="small" />}
                      sx={{ textTransform: "none", fontSize: "0.75rem" }}
                    >
                      Reactivar
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}

            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3, fontStyle: "italic" }}>
                    No se encontraron registros con los filtros aplicados.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={(_, newPage) => onPageChange(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => onRowsPerPageChange(parseInt(e.target.value, 10))}
        rowsPerPageOptions={[rowsPerPage]}
      />

      <ModalComponent open={contextModal.open} handleClose={() => setContextModal({ open: false, row: null })} width="500px">
        <Typography variant="subtitle1" fontWeight={600} mb={2}>
          Contexto de la asignación del cuestionario
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={1}>
          {contextModal.row?.questionnaireTitle}
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Box sx={{ maxHeight: "60vh", overflowY: "auto", pr: 1 }}>{renderContextContent(contextModal.row?.questionnaireContext)}</Box>
      </ModalComponent>
    </Box>
  );
};

export default AssignmentsTable;
